import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/* ─────────────────────────────────────────────────────────────────────────
 *  Theme (light / dark / grey)
 *
 *  Applies theme classes on <html> and persists the choice to localStorage.
 *  Starts light when nothing is stored. To avoid
 *  a light→dark flash, the SSR shell (`Html.tsx`) runs a blocking inline script
 *  that sets the class before first paint — this module must read the SAME
 *  storage key + default so client and server agree.
 *
 *  `grey` is a variant of dark, not a third independent theme: it carries BOTH
 *  classes, so every `dark:` utility and every `.dark` token still applies and
 *  `.grey` only re-tints the neutrals. Dropping `dark` here would strip the
 *  whole dark theme and leave a light UI with a black background.
 * ───────────────────────────────────────────────────────────────────────── */

export type Theme = 'light' | 'dark' | 'grey';

const THEMES: Theme[] = ['light', 'dark', 'grey'];

/** Themes that render on dark surfaces. */
const isDarkTheme = (theme: Theme): boolean => theme !== 'light';

export const THEME_STORAGE_KEY = 'marble-theme';

export const readStoredTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored && (THEMES as string[]).includes(stored)) return stored as Theme;
  return 'light';
};

export const applyThemeClass = (theme: Theme): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', isDarkTheme(theme));
  root.classList.toggle('grey', theme === 'grey');
};

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyThemeClass(theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* localStorage unavailable (private mode / quota) — theme still applies
       * for the session, it just won't persist. */
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => setThemeState(next), []);
  /** Cycles through the available themes in order. */
  const toggleTheme = useCallback(
    () =>
      setThemeState(
        (prev) => THEMES[(THEMES.indexOf(prev) + 1) % THEMES.length],
      ),
    [],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};
